import type { ImageMeta } from '@didto/domain';
import { fractionDigitsFor, parseMoneyToMinor } from '@didto/domain';
import { Button, Input } from '@didto/ui';
import { useState } from 'react';
import { useToast } from '@/app/toast';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { catalogImageUrl, uploadCatalogImage } from '@/lib/catalog';
import './catalog.css';

/** Text input for an amount in the business currency; the parent parses it with parseMoneyToMinor. */
export function MoneyInput({
  id,
  value,
  onChange,
  currency,
  allowNegative,
  testId,
  required,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  currency: string;
  allowNegative?: boolean;
  testId?: string;
  required?: boolean;
}) {
  const invalid =
    value.trim() !== '' &&
    parseMoneyToMinor(value, {
      fractionDigits: fractionDigitsFor(currency),
      ...(allowNegative ? { allowNegative: true } : {}),
    }) === null;
  return (
    <div className="catalog__money">
      <span className="d-muted">{currency}</span>
      <Input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={fractionDigitsFor(currency) === 0 ? '0' : '0.00'}
        aria-invalid={invalid || undefined}
        required={required}
        className="d-input--money catalog__money-input"
        data-testid={testId}
      />
    </div>
  );
}

/** Amount text -> minor units, or a message the form can show. */
export function moneyOrError(
  text: string,
  currency: string,
  options: { required?: boolean; allowNegative?: boolean } = {},
): { minor: number | null; error: string | null } {
  if (text.trim() === '') {
    return options.required
      ? { minor: null, error: 'Enter an amount' }
      : { minor: null, error: null };
  }
  const minor = parseMoneyToMinor(text, {
    fractionDigits: fractionDigitsFor(currency),
    ...(options.allowNegative ? { allowNegative: true } : {}),
  });
  return minor === null ? { minor: null, error: 'Enter a valid amount' } : { minor, error: null };
}

/** Upload-or-remove control for a catalog image. Records nothing itself; reports the new value. */
export function ImageField({
  businessId,
  path,
  onChange,
  label = 'Image',
}: {
  businessId: string;
  path: string | null;
  onChange: (next: { path: string | null; meta: ImageMeta | null }) => void;
  label?: string;
}) {
  const { toast } = useToast();
  // ADR-037: an upload needs the server; removing only changes the form until it is saved
  const online = useOnline();
  const [busy, setBusy] = useState(false);
  const url = catalogImageUrl(path);
  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadCatalogImage(businessId, file);
      onChange({ path: up.path, meta: up.meta });
    } catch (e) {
      toast(errorText(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  const off = busy || !online;
  return (
    <div className="d-field">
      <span className="d-label">{label}</span>
      <div className="d-inline">
        {url ? (
          <img src={url} alt="" width={72} height={72} className="catalog__image" />
        ) : (
          <span className="d-muted">No image</span>
        )}
        <label
          className={`d-btn d-btn--ghost d-btn--sm catalog__upload${off ? ' catalog__upload--off' : ''}`}
          aria-disabled={off || undefined}
        >
          {busy ? 'Uploading…' : url ? 'Replace' : 'Upload'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="d-sr-only"
            disabled={off}
            onChange={(e) => void pick(e.target.files?.[0])}
            data-testid="image-file"
          />
        </label>
        {url && (
          <Button
            size="sm"
            variant="quiet"
            disabled={busy}
            onClick={() => onChange({ path: null, meta: null })}
          >
            Remove
          </Button>
        )}
      </div>
      <div className="d-help">
        JPEG, PNG or WebP up to 5 MB. Square images look best on the POS.
      </div>
    </div>
  );
}
